import { Form } from 'react-bootstrap';
import Editor, { type OnChange, type OnMount } from '@monaco-editor/react';
import { useMemo } from 'react';
import { useAppContext } from 'providers/AppProvider';

// Static Python builtins surfaced as completions. Monaco ships syntax
// highlighting for Python but no builtins library, so we register a tiny
// provider once per app load.
const PYTHON_BUILTINS = [
  'abs',
  'all',
  'any',
  'ascii',
  'bin',
  'bool',
  'bytearray',
  'bytes',
  'callable',
  'chr',
  'classmethod',
  'compile',
  'complex',
  'delattr',
  'dict',
  'dir',
  'divmod',
  'enumerate',
  'eval',
  'exec',
  'filter',
  'float',
  'format',
  'frozenset',
  'getattr',
  'globals',
  'hasattr',
  'hash',
  'help',
  'hex',
  'id',
  'input',
  'int',
  'isinstance',
  'issubclass',
  'iter',
  'len',
  'list',
  'locals',
  'map',
  'max',
  'memoryview',
  'min',
  'next',
  'object',
  'oct',
  'open',
  'ord',
  'pow',
  'print',
  'property',
  'range',
  'repr',
  'reversed',
  'round',
  'set',
  'setattr',
  'slice',
  'sorted',
  'staticmethod',
  'str',
  'sum',
  'super',
  'tuple',
  'type',
  'vars',
  'zip',
  'True',
  'False',
  'None',
  'NotImplemented',
  'Ellipsis',
  '__import__',
];

let pythonProviderRegistered = false;
const registerPythonBuiltins = (monaco: Parameters<OnMount>[1]) => {
  if (pythonProviderRegistered) return;
  pythonProviderRegistered = true;
  monaco.languages.registerCompletionItemProvider('python', {
    provideCompletionItems: (model, position) => {
      const word = model.getWordUntilPosition(position);
      const range = {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endColumn: word.endColumn,
      };
      return {
        suggestions: PYTHON_BUILTINS.map((name) => ({
          label: name,
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: name,
          range,
          detail: 'Python builtin',
        })),
      };
    },
  });
};

export type ScriptEditorLanguage = 'shell' | 'sql' | 'python' | 'json';

interface ScriptEditorProps {
  label?: string;
  controlId: string;
  name: string;
  value: string;
  language: ScriptEditorLanguage;
  onChange: (e: { target: { name: string; value: string } }) => void;
  readOnly?: boolean;
  minHeight?: number;
  maxHeight?: number;
  groupClassName?: string;
  'aria-label'?: string;
  required?: boolean;
}

const LINE_HEIGHT = 19;
const PADDING = 20;

const ScriptEditor: React.FC<ScriptEditorProps> = ({
  label,
  controlId,
  name,
  value = '',
  language,
  onChange,
  readOnly = false,
  minHeight = 400,
  maxHeight = 1000,
  groupClassName = 'mb-2 w-full',
  'aria-label': ariaLabel,
  required = false,
}) => {
  const height = useMemo(() => {
    const lines = (value || '').split('\n').length;
    const calc = lines * LINE_HEIGHT + PADDING;
    return Math.min(maxHeight, Math.max(minHeight, calc));
  }, [value, minHeight, maxHeight]);

  const {
    config: { theme },
  } = useAppContext();
  const monacoTheme = theme === 'dark' ? 'vs-dark' : 'vs';

  const handleChange: OnChange = (next) => {
    // Normalize to LF. Monaco preserves the source EOL (and snaps a CRLF-dominant
    // file to all-CRLF), but downstream AIX consumers only accept LF.
    onChange({ target: { name, value: (next ?? '').replace(/\r\n/g, '\n') } });
  };

  const handleMount: OnMount = (_editor, monaco) => {
    if (language === 'python') registerPythonBuiltins(monaco);
  };

  return (
    <Form.Group className={groupClassName} controlId={controlId}>
      {label && (
        <Form.Label>
          {label}
          {required && <span className="text-danger"> *</span>}
        </Form.Label>
      )}
      <div
        aria-label={ariaLabel || label || name}
        style={{
          border: '1px solid var(--phoenix-border-color, #cdd0d5)',
          borderRadius: '0.375rem',
          overflow: 'hidden',
          backgroundColor: theme === 'dark' ? '#1e1e1e' : '#ffffff',
        }}
      >
        <Editor
          height={height}
          language={language}
          value={value}
          onChange={handleChange}
          onMount={handleMount}
          theme={monacoTheme}
          options={{
            readOnly,
            minimap: { enabled: true, renderCharacters: false },
            lineNumbers: 'on',
            scrollBeyondLastLine: false,
            wordWrap: 'on',
            automaticLayout: true,
            fontSize: 13,
            fontFamily: '"Fira Code", "Fira Mono", monospace',
            tabSize: 2,
            renderLineHighlight: 'line',
            bracketPairColorization: { enabled: true },
            matchBrackets: 'always',
            autoIndent: 'full',
            formatOnPaste: true,
            scrollbar: { alwaysConsumeMouseWheel: false },
          }}
        />
      </div>
    </Form.Group>
  );
};

export default ScriptEditor;
